import { useState } from 'react';
import {
  DillField,
  DillSelect,
  DillInput,
  DillButton,
  DillToggle,
  DillModal,
} from '../../../general/Dill';
import { openDillToast } from '../../../../utils/helpers';
import { APP_ENVIRONMENT } from '../../../../utils/constants';

// ─────────────────────────────────────────────────────────────────────────────
// DEMO CONCEPT - not wired to any backend. "Automated Credit Review Triggers" would watch
// customers for a time or event condition and, when one fires, kick off the same flow as the
// "Request Update" button on Credit Applications (RequestApplicationUpdateModal.tsx) - emailing
// the customer to review/confirm their credit application - but automatically instead of a
// credit manager clicking it by hand. There's no backend model or job for this yet; everything
// below lives in local component state so the concept can be demoed/tested on the Demo Site
// without promising functionality that doesn't exist in production.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Types ────────────────────────────────────────────────────────────────────

type TriggerCategory = 'TIME_BASED' | 'EVENT_BASED';

type TimeFrequencyKey =
  | 'EVERY_6_MONTHS'
  | 'EVERY_1_YEAR'
  | 'EVERY_2_YEARS'
  | 'EVERY_3_YEARS'
  | 'CUSTOM';

type EventConditionKey =
  | 'NO_ACTIVITY_YEARS'
  | 'STALE_CREDIT_APP_YEARS'
  | 'CREDIT_SCORE_DROP_PERCENT'
  | 'OVER_UTILIZATION_PERCENT'
  | 'DSO_THRESHOLD_DAYS'
  | 'DELINQUENCY_LEVEL_PERCENT';

type ReviewTrigger = {
  id: string;
  name: string;
  category: TriggerCategory;
  isActive: boolean;
  frequency: TimeFrequencyKey;
  customMonths: string;
  conditionKey: EventConditionKey;
  thresholdValue: string;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const TIME_FREQUENCY_OPTIONS: { label: string; value: TimeFrequencyKey }[] = [
  { label: 'Every 6 months', value: 'EVERY_6_MONTHS' },
  { label: 'Annually', value: 'EVERY_1_YEAR' },
  { label: 'Every 2 years', value: 'EVERY_2_YEARS' },
  { label: 'Every 3 years', value: 'EVERY_3_YEARS' },
  { label: 'Custom', value: 'CUSTOM' },
];

const EVENT_CONDITION_OPTIONS: {
  label: string;
  value: EventConditionKey;
  valueLabel: string;
  unit: string;
  describe: (value: string) => string;
}[] = [
  {
    label: "Hasn't been invoiced / bought anything",
    value: 'NO_ACTIVITY_YEARS',
    valueLabel: 'Years of inactivity',
    unit: 'years',
    describe: (v) => `No invoices or purchases in the last ${v || 'X'} year${v === '1' ? '' : 's'}`,
  },
  {
    label: 'Credit application is stale',
    value: 'STALE_CREDIT_APP_YEARS',
    valueLabel: 'Years since last application',
    unit: 'years',
    describe: (v) => `Last credit application was ${v || 'X'} year${v === '1' ? '' : 's'} ago`,
  },
  {
    label: 'Credit score has dropped',
    value: 'CREDIT_SCORE_DROP_PERCENT',
    valueLabel: 'Drop threshold',
    unit: '%',
    describe: (v) => `Credit score has dropped ${v || 'X'}% or more`,
  },
  {
    label: 'Credit utilization is over 100%',
    value: 'OVER_UTILIZATION_PERCENT',
    valueLabel: 'Over-limit threshold',
    unit: '%',
    describe: (v) => `Credit utilization exceeds 100% by ${v || 'X'}% or more`,
  },
  {
    label: 'DSO has gotten worse',
    value: 'DSO_THRESHOLD_DAYS',
    valueLabel: 'DSO threshold',
    unit: 'days',
    describe: (v) => `Days Sales Outstanding (DSO) exceeds ${v || 'X'} days`,
  },
  {
    label: "Customer's payments are delinquent",
    value: 'DELINQUENCY_LEVEL_PERCENT',
    valueLabel: 'Delinquent balance threshold',
    unit: '%',
    describe: (v) => `${v || 'X'}% or more of the customer's balance is past due`,
  },
];

let uid = 0;
const nextId = () => `demo-trigger-${++uid}`;

const blankTrigger = (): ReviewTrigger => ({
  id: nextId(),
  name: '',
  category: 'TIME_BASED',
  isActive: true,
  frequency: 'EVERY_1_YEAR',
  customMonths: '',
  conditionKey: 'NO_ACTIVITY_YEARS',
  thresholdValue: '',
});

const DEFAULT_TRIGGERS: Omit<ReviewTrigger, 'id'>[] = [
  {
    name: 'Annual credit review',
    category: 'TIME_BASED',
    isActive: true,
    frequency: 'EVERY_1_YEAR',
    customMonths: '',
    conditionKey: 'NO_ACTIVITY_YEARS',
    thresholdValue: '',
  },
  {
    name: 'Biennial credit review',
    category: 'TIME_BASED',
    isActive: false,
    frequency: 'EVERY_2_YEARS',
    customMonths: '',
    conditionKey: 'NO_ACTIVITY_YEARS',
    thresholdValue: '',
  },
  {
    name: 'Inactive customer',
    category: 'EVENT_BASED',
    isActive: true,
    frequency: 'EVERY_1_YEAR',
    customMonths: '',
    conditionKey: 'NO_ACTIVITY_YEARS',
    thresholdValue: '2',
  },
  {
    name: 'Stale credit application',
    category: 'EVENT_BASED',
    isActive: true,
    frequency: 'EVERY_1_YEAR',
    customMonths: '',
    conditionKey: 'STALE_CREDIT_APP_YEARS',
    thresholdValue: '3',
  },
  {
    name: 'Credit score dropped',
    category: 'EVENT_BASED',
    isActive: false,
    frequency: 'EVERY_1_YEAR',
    customMonths: '',
    conditionKey: 'CREDIT_SCORE_DROP_PERCENT',
    thresholdValue: '15',
  },
  {
    name: 'Over-utilized customer',
    category: 'EVENT_BASED',
    isActive: true,
    frequency: 'EVERY_1_YEAR',
    customMonths: '',
    conditionKey: 'OVER_UTILIZATION_PERCENT',
    thresholdValue: '10',
  },
  {
    name: 'Rising DSO',
    category: 'EVENT_BASED',
    isActive: true,
    frequency: 'EVERY_1_YEAR',
    customMonths: '',
    conditionKey: 'DSO_THRESHOLD_DAYS',
    thresholdValue: '45',
  },
  {
    name: 'Delinquent customer',
    category: 'EVENT_BASED',
    isActive: true,
    frequency: 'EVERY_1_YEAR',
    customMonths: '',
    conditionKey: 'DELINQUENCY_LEVEL_PERCENT',
    thresholdValue: '20',
  },
];

// ─── Small icons (kept local, not shared - mirrors ScorecardSettings.tsx's own copies) ────────

const TrashIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4h6v2" />
  </svg>
);

const PencilIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconBtn = ({
  onClick,
  variant,
  children,
}: {
  onClick: () => void;
  variant: 'default' | 'danger';
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={`w-7 h-7 flex items-center justify-center border rounded-md transition-colors shrink-0 ${
      variant === 'danger'
        ? 'border-red-200 bg-red-50 text-red-500 hover:bg-red-100'
        : 'border-gray-200 bg-white text-gray-400 hover:text-blue-600 hover:bg-blue-50'
    }`}>
    {children}
  </button>
);

const describeTrigger = (t: ReviewTrigger): string => {
  if (t.category === 'TIME_BASED') {
    const opt = TIME_FREQUENCY_OPTIONS.find((f) => f.value === t.frequency);
    const cadence =
      t.frequency === 'CUSTOM'
        ? `Every ${t.customMonths || 'X'} months`
        : opt?.label ?? 'On a schedule';
    return `${cadence} → request a credit review`;
  }
  const opt = EVENT_CONDITION_OPTIONS.find((c) => c.value === t.conditionKey);
  return `When ${(opt?.describe ?? (() => ''))(t.thresholdValue)} → request a credit review`;
};

// ─── Add/Edit modal ───────────────────────────────────────────────────────────

function TriggerEditorModal({
  mode,
  trigger,
  onClose,
  onSave,
}: {
  mode: 'add' | 'edit';
  trigger: ReviewTrigger;
  onClose: () => void;
  onSave: (t: ReviewTrigger) => void;
}) {
  const [draft, setDraft] = useState<ReviewTrigger>(trigger);
  const upd = (u: Partial<ReviewTrigger>) => setDraft((d) => ({ ...d, ...u }));
  const eventOpt = EVENT_CONDITION_OPTIONS.find((c) => c.value === draft.conditionKey);

  return (
    <DillModal
      open
      onClose={onClose}
      title={mode === 'add' ? 'Add Credit Review Trigger' : 'Edit Credit Review Trigger'}
      maxWidth="520px"
      footer={
        <>
          <DillButton variant="secondary" label="Cancel" onClick={onClose} />
          <DillButton
            label={mode === 'add' ? 'Add Trigger' : 'Save Changes'}
            onClick={() => onSave(draft)}
            isDisabled={!draft.name.trim()}
          />
        </>
      }>
      <DillField label="Name">
        <DillInput
          value={draft.name}
          onChange={(v) => upd({ name: v })}
          placeholder="e.g. Annual credit review"
        />
      </DillField>

      <DillField label="Trigger type">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => upd({ category: 'TIME_BASED' })}
            className={`flex-1 text-sm font-medium rounded-lg border px-3 py-2 transition-colors ${
              draft.category === 'TIME_BASED'
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}>
            Time-based
          </button>
          <button
            type="button"
            onClick={() => upd({ category: 'EVENT_BASED' })}
            className={`flex-1 text-sm font-medium rounded-lg border px-3 py-2 transition-colors ${
              draft.category === 'EVENT_BASED'
                ? 'bg-purple-50 border-purple-300 text-purple-700'
                : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}>
            Event-based
          </button>
        </div>
      </DillField>

      {draft.category === 'TIME_BASED' ? (
        <>
          <DillField label="Frequency">
            <DillSelect
              value={draft.frequency}
              onChange={(v) => upd({ frequency: v as TimeFrequencyKey })}
              options={TIME_FREQUENCY_OPTIONS}
            />
          </DillField>
          {draft.frequency === 'CUSTOM' && (
            <DillField label="Every X months">
              <DillInput
                type="number"
                value={draft.customMonths}
                onChange={(v) => upd({ customMonths: v })}
                placeholder="e.g. 18"
              />
            </DillField>
          )}
        </>
      ) : (
        <>
          <DillField label="Condition">
            <DillSelect
              value={draft.conditionKey}
              onChange={(v) => upd({ conditionKey: v as EventConditionKey })}
              options={EVENT_CONDITION_OPTIONS.map((c) => ({ label: c.label, value: c.value }))}
            />
          </DillField>
          <DillField label={`${eventOpt?.valueLabel ?? 'Threshold'} (${eventOpt?.unit ?? ''})`}>
            <DillInput
              type="number"
              value={draft.thresholdValue}
              onChange={(v) => upd({ thresholdValue: v })}
              placeholder="e.g. 2"
            />
          </DillField>
        </>
      )}

      <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
        <span className="text-xs font-medium text-gray-600">Active</span>
        <DillToggle value={draft.isActive} onChange={(v) => upd({ isActive: v })} />
      </div>

      <div className="rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2.5">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
          Preview
        </p>
        <p className="text-xs text-gray-600 italic">{describeTrigger(draft)}</p>
      </div>
    </DillModal>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AutomatedCreditReviewTriggers() {
  const [triggers, setTriggers] = useState<ReviewTrigger[]>(() =>
    DEFAULT_TRIGGERS.map((t) => ({ ...t, id: nextId() }))
  );
  const [modalState, setModalState] = useState<{
    mode: 'add' | 'edit';
    draft: ReviewTrigger;
  } | null>(null);

  const removeTrigger = (id: string) => setTriggers((prev) => prev.filter((t) => t.id !== id));

  const toggleActive = (id: string, isActive: boolean) =>
    setTriggers((prev) => prev.map((t) => (t.id === id ? { ...t, isActive } : t)));

  const handleModalSave = (saved: ReviewTrigger) => {
    setTriggers((prev) =>
      modalState?.mode === 'add'
        ? [...prev, saved]
        : prev.map((t) => (t.id === saved.id ? saved : t))
    );
    setModalState(null);
  };

  const handleSave = () => {
    // Demo only - nothing is persisted. Real triggers, once built, would need their own backend
    // model + a scheduled job to evaluate them, similar to CollectionTrigger/CollectionSequence.
    openDillToast({
      type: 'SUCCESS',
      message: "Saved (demo only) — automated triggers aren't wired up to run yet",
    });
  };

  // Demo-concept only - guarded here too (not just at the ApprovalWorkflows.tsx call site), so
  // this never renders in production even if something else ends up rendering it directly.
  // Kept after the hooks above (not as an early return before them), per the rules of hooks.
  if (APP_ENVIRONMENT === 'production') return null;

  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      {modalState && (
        <TriggerEditorModal
          mode={modalState.mode}
          trigger={modalState.draft}
          onClose={() => setModalState(null)}
          onSave={handleModalSave}
        />
      )}

      <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-900">Automated Credit Review Triggers</p>
          <p className="text-xs text-gray-400 mt-0.5 max-w-2xl">
            Automatically request a credit review — the same &quot;Request Update&quot; flow used on
            individual Credit Applications — when a customer meets a rule below, instead of a credit
            manager triggering it by hand.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <DillButton onClick={handleSave} label="Save" variant="primary" />
        </div>
      </div>

      <div className="p-5 flex flex-col gap-2">
        {triggers.map((t) => (
          <div
            key={t.id}
            className="flex items-center gap-3 px-4 py-3 border border-gray-100 rounded-lg bg-white hover:bg-gray-50 transition-colors">
            <DillToggle value={t.isActive} onChange={(v) => toggleActive(t.id, v)} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-800 truncate">{t.name}</p>
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 ${
                    t.category === 'TIME_BASED'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                  {t.category === 'TIME_BASED' ? 'Time-based' : 'Event-based'}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5 truncate">{describeTrigger(t)}</p>
            </div>
            <IconBtn variant="default" onClick={() => setModalState({ mode: 'edit', draft: t })}>
              <PencilIcon />
            </IconBtn>
            <IconBtn variant="danger" onClick={() => removeTrigger(t.id)}>
              <TrashIcon />
            </IconBtn>
          </div>
        ))}

        <DillButton
          variant="secondary"
          label="Add trigger"
          icon={<PlusIcon />}
          onClick={() => setModalState({ mode: 'add', draft: blankTrigger() })}
        />

        <p className="text-[11px] text-gray-400 mt-1">
          Applies to: all customers (per-customer/segment scoping, and actually running these on a
          schedule, would come with the real version of this feature).
        </p>
      </div>
    </div>
  );
}
